import { Box, Container, Stack, Text, Title, SimpleGrid, List } from '@mantine/core'
import {
  IoShieldCheckmarkOutline,
  IoConstructOutline,
  IoChatbubblesOutline
} from 'react-icons/io5'
import { SiteLegalFooter } from '../../app/common/ui'
import bulletPointIconUrl from '../../app/icon-9mm-bulletpoint/icon-styled.svg?url'

const CROSSHAIR = {
  backgroundImage:
    'repeating-linear-gradient(0deg, rgba(232,93,4,0.04) 0px, transparent 1px, transparent 20px), repeating-linear-gradient(90deg, rgba(232,93,4,0.04) 0px, transparent 1px, transparent 20px)'
}

const PRINCIPLES: Array<{ title: string; body: string; icon: React.ReactNode }> = [
  {
    title: 'Community-verified information',
    body: 'Every firearm in the Registry, every part in the Parts Bin, and every build shared on GunSlop comes from the community. Users contribute, verify, and improve the data. The AmmoScore system ensures that the most reliable contributors earn standing on the platform.',
    icon: <IoShieldCheckmarkOutline size={26} color="var(--gs-accent)" />
  },
  {
    title: 'Built for real use',
    body: 'We exist to solve problems that gun owners actually face. Finding accurate specifications on a specific firearm. Understanding part compatibility. Seeing how others have configured similar builds. These are the things GunSlop makes simple.',
    icon: <IoConstructOutline size={26} color="var(--gs-accent)" />
  },
  {
    title: 'All firearm discussions welcome',
    body: "GunSlop is a space for firearm-related conversation. Whether you're discussing technical specifications, design choices, parts selection, regulation, or anything else related to firearms — that discussion belongs here. Stay on topic. That's the only rule.",
    icon: <IoChatbubblesOutline size={26} color="var(--gs-accent)" />
  }
]

const PROVIDES = [
  'A searchable Registry of firearms with community-verified data',
  'A Parts Bin documenting compatibility and real-world information',
  'Build documentation from owners showing complete configurations',
  'A reputation system that surfaces reliable voices',
  'Tools to share your gear safely, including serial number detection'
]

export default function AboutUsPage () {
  return (
    <Box sx={{ marginTop: -8 }}>
      <Box
        sx={{
          width: '100%',
          ...CROSSHAIR,
          padding: '48px 20px 56px',
          borderBottom: '1px solid var(--gs-border)'
        }}
        style={{ background: 'var(--gs-bg)' }}
      >
        <Container size="sm">
          <Stack spacing="lg" align="center">
            <Text
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: 3,
                textTransform: 'uppercase',
                color: 'var(--gs-muted)'
              }}
            >
              About GunSlop
            </Text>
            <Title
              order={1}
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontWeight: 700,
                fontSize: 'clamp(28px, 4.5vw, 40px)',
                color: 'var(--gs-text)',
                textAlign: 'center',
                lineHeight: 1.15
              }}
            >
              A community platform for
              <br />
              <Text component="span" sx={{ color: 'var(--gs-accent)' }}>
                firearm lovers
              </Text>
            </Title>
            <Text
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 16,
                color: 'var(--gs-muted)',
                textAlign: 'center',
                maxWidth: 520,
                lineHeight: 1.7
              }}
            >
              GunSlop is a community platform built for firearm enthusiasts. We provide tools to document, verify,
              and share knowledge about firearms, components, and builds. Our goal is to make that information
              accessible and reliable.
            </Text>
          </Stack>
        </Container>
      </Box>

      <Box sx={{ padding: '48px 20px 56px' }}>
        <Container size="lg">
          <SimpleGrid cols={3} spacing="lg" breakpoints={[{ maxWidth: 'md', cols: 1 }]}>
            {PRINCIPLES.map((p) => (
              <Box
                key={p.title}
                sx={{
                  padding: '22px 20px',
                  background: 'var(--gs-surface)',
                  border: '1px solid var(--gs-border)',
                  borderRadius: 8,
                  transition: 'border-color 150ms ease',
                  '&:hover': { borderColor: 'var(--gs-border-bright)' }
                }}
              >
                <Box sx={{ marginBottom: 14 }}>{p.icon}</Box>
                <Text
                  sx={{
                    fontFamily: 'Oswald, sans-serif',
                    fontWeight: 600,
                    fontSize: 15,
                    letterSpacing: 0.3,
                    color: 'var(--gs-text)',
                    marginBottom: 12,
                    lineHeight: 1.25
                  }}
                >
                  {p.title}
                </Text>
                <Text
                  sx={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: 14,
                    color: 'var(--gs-muted)',
                    lineHeight: 1.65
                  }}
                >
                  {p.body}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      <Box
        sx={{
          padding: '48px 20px 56px',
          background: 'var(--gs-surface)',
          borderTop: '1px solid var(--gs-border)',
          borderBottom: '1px solid var(--gs-border)'
        }}
      >
        <Container size="md">
          <Title
            order={2}
            sx={{
              fontFamily: 'Oswald, sans-serif',
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: 2.5,
              textTransform: 'uppercase',
              color: 'var(--gs-muted)',
              marginBottom: 20
            }}
          >
            What we provide
          </Title>
          <List
            spacing="sm"
            size="sm"
            icon={
              <Box
                component="img"
                src={bulletPointIconUrl}
                alt=""
                aria-hidden
                sx={{
                  width: 22,
                  height: 22,
                  display: 'block',
                  objectFit: 'contain',
                  flexShrink: 0
                }}
              />
            }
            styles={{
              item: { alignItems: 'flex-start' },
              itemWrapper: { width: '100%' }
            }}
          >
            {PROVIDES.map((line) => (
              <List.Item key={line}>
                <Text sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 15, color: 'var(--gs-text)', lineHeight: 1.55 }}>
                  {line}
                </Text>
              </List.Item>
            ))}
          </List>
        </Container>
      </Box>

      <SiteLegalFooter />
    </Box>
  )
}
